import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Grid,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from '@mui/material';
import { Download } from '@mui/icons-material';
import { useEnergy } from '../context/EnergyContext';
import { formatValue } from '../utils/energyViewModel';
import { EnergyLineChart, EnergyPageHeader, MetricTile, ProvenanceNote, SectionCard } from '../components/energy/EnergyUi';

const tabs = [
  'Energy balance',
  'Demand',
  'Battery performance',
  'Cost and value',
  'Events and reliability',
];

const csvValue = (value) => {
  if (value === null || value === undefined) return '';
  return `"${String(value).replace(/"/g, '""')}"`;
};

export default function EnergyReportsView() {
  const { model, range, raw, scenario, setScenario } = useEnergy();
  const summary = raw?.costSummary?.data || {};
  const scenarios = raw?.costInputs?.profile?.outageAssumptions?.filter(item => item.enabled) || [];
  const lineItems = [
    ...(summary.periodUtilityCost?.lineItems || []).map(item => ({ ...item, kind: 'Utility' })),
    ...(summary.periodLeaseCost?.lineItems || []).map(item => ({ ...item, kind: 'Lease' })),
  ];
  const lease = model.leaseEconomics || {};
  const currency = lease.currency || 'USD';
  const billingMonth = lease.billingMonth?.start ? new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric', timeZone: lease.billingMonth.timezone || 'UTC' }).format(new Date(lease.billingMonth.start)) : '-';
  const hasUtilityAnalysis = [model.costs.importEnergyPricePerKwh, model.costs.exportCreditPerKwh, model.costs.utilityRatePerHour, model.costs.periodUtilityCost, model.costs.averageUtilityCostPerKwh, model.costs.modeledLcoePerKwh, model.costs.outageEconomicValue].some((value) => value !== null && value !== undefined);
  const [tab, setTab] = useState(0);
  const rows = useMemo(() => model.rollups.slice(-50), [model.rollups]);

  const exportVisibleCsv = () => {
    if (!rows.length) return;
    const keys = ['time', 'periodEnd', 'metric', 'value', 'unit', 'status', 'sourceKind', 'coveragePct', 'revision', 'calculationVersion', 'configurationVersion', 'profileId', 'profileVersion', 'cutoffTime', 'watermarkCommitSeq', 'sourceWatermark'];
    const csv = [keys.join(','), ...rows.map((row) => keys.map((key) => csvValue(typeof row[key] === 'object' ? JSON.stringify(row[key]) : row[key])).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `someac-energy-${typeof range === 'string' ? range : 'custom'}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const actions = <Button size="small" variant="contained" startIcon={<Download />} onClick={exportVisibleCsv} disabled={!rows.length}>Raw CSV</Button>;
  return (
    <Box>
      <EnergyPageHeader title="Energy Reports" subtitle="Authorized energy history, accounting evidence, cost lineage, and reliability events" actions={actions} showRange />
      <Grid container spacing={1} sx={{ mb: 2 }}>
        <Grid item xs={6} md={2}><MetricTile label="Selected period generation" value={model.energy.generatedKwh} measure={model.measures?.energy?.generatedKwh} unit="kWh" accent="#f57c00" provenance={model.measures?.energy?.generatedKwh?.sourceKind === 'counter_derived' ? 'Register-derived' : 'Estimated'} /></Grid>
        <Grid item xs={6} md={2}><MetricTile label="Selected period consumption" value={model.energy.consumedKwh} measure={model.measures?.energy?.consumedKwh} unit="kWh" accent="#7b1fa2" /></Grid>
        <Grid item xs={6} md={2}><MetricTile label="Selected period imported" value={model.energy.importedKwh} measure={model.measures?.energy?.importedKwh} unit="kWh" accent="#1976d2" /></Grid>
        <Grid item xs={6} md={2}><MetricTile label="Selected period exported" value={model.energy.exportedKwh} measure={model.measures?.energy?.exportedKwh} unit="kWh" accent="#0288d1" /></Grid>
        <Grid item xs={6} md={2}><MetricTile label="Selected period BESS delivery" value={model.energy.dischargedKwh} measure={model.measures?.energy?.dischargedKwh} unit="kWh" accent="#388e3c" /></Grid>
        <Grid item xs={6} md={2}><MetricTile label={lease.proposalStatus === 'contractual' ? 'Monthly lease payment' : 'Proposed monthly lease payment'} value={lease.monthlyLeasePayment} unit={`${currency}/month`} accent="#005dac" /></Grid>
      </Grid>

      <Paper variant="outlined" sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={(_, value) => setTab(value)} variant="scrollable" scrollButtons="auto" aria-label="Energy report sections">
          {tabs.map((label) => <Tab key={label} label={label} />)}
        </Tabs>
      </Paper>

      {tab === 0 && (
        <Stack spacing={2}>
          <SectionCard title="Period energy flow and power balance" subtitle="Physical directions are kept separate; chart gaps are not zero-filled">
            <EnergyLineChart series={model.series} fields={[{ key: 'solarKw', label: 'Solar generation' }, { key: 'loadKw', label: 'Facility load' }, { key: 'bessKw', label: 'Battery charge / discharge' }, { key: 'gridKw', label: 'Grid import / export' }]} height={310} />
          </SectionCard>
          <SectionCard title="Production source comparison">
            <Grid container spacing={1}>
              <Grid item xs={12} sm={6}><MetricTile label="Inverter lifetime-counter total" value={model.energy.inverterRegisterKwh} measure={model.measures?.energy?.inverterRegisterKwh} unit="kWh" provenance="Register-derived" accent="#f57c00" /></Grid>
              <Grid item xs={12} sm={6}><MetricTile label="Sampled-power integration" value={model.energy.sampledEstimateKwh} measure={model.measures?.energy?.sampledEstimateKwh} unit="kWh" provenance="Estimated" accent="#ed6c02" /></Grid>
            </Grid>
          </SectionCard>
        </Stack>
      )}

      {tab === 1 && (
        <SectionCard title="Demand and peak analysis">
          <EnergyLineChart series={model.series} fields={[{ key: 'loadKw', label: 'Facility load' }, { key: 'solarKw', label: 'Solar generation' }, { key: 'bessKw', label: 'Battery response' }]} height={320} />
          <Grid container spacing={1} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}><MetricTile label="Interval peak" value={model.load.intervalPeakKw} unit="kW" /></Grid>
            <Grid item xs={12} sm={6}><MetricTile label="Billing-period peak" value={model.load.billingPeakKw} unit="kW" /></Grid>
          </Grid>
        </SectionCard>
      )}

      {tab === 2 && (
        <SectionCard title="Battery performance">
          <EnergyLineChart series={model.series} fields={[{ key: 'socPct', label: 'SOC' }, { key: 'bessKw', label: 'Battery power' }]} height={320} />
          <Grid container spacing={1} sx={{ mt: 1 }}>
            <Grid item xs={6}><MetricTile label="Charged" value={model.energy.chargedKwh} measure={model.measures?.energy?.chargedKwh} unit="kWh" /></Grid>
            <Grid item xs={6}><MetricTile label="Discharged" value={model.energy.dischargedKwh} measure={model.measures?.energy?.dischargedKwh} unit="kWh" /></Grid>
          </Grid>
        </SectionCard>
      )}

      {tab === 3 && (
        <Stack spacing={2}>
          <SectionCard title="Economics" subtitle={`${lease.assetScope || '-'} · ${billingMonth}`}>
            <Grid container spacing={1}>
              {[
                [lease.proposalStatus === 'contractual' ? 'Monthly lease payment' : 'Proposed monthly lease payment', lease.monthlyLeasePayment, `${currency}/month`],
                ['Separately payable operating cost', lease.monthlyOperatingCost, `${currency}/month`],
                ['Monthly system cost', lease.monthlySystemCost, `${currency}/month`],
                ['Daily system cost', lease.dailySystemCost, `${currency}/day`],
                ['System cost run rate', lease.currentSystemCostRate, `${currency}/h`],
                ['Cost this month', lease.accruedMonthCost, currency],
                ['Current generation cost', lease.currentGenerationCostPerKwh, `${currency}/kWh generated`],
                ['Average generation cost this month', lease.averageGenerationCostThisMonthPerKwh, `${currency}/kWh generated`],
              ].map(([label, value, unit]) => <Grid item xs={12} sm={6} md={4} key={label}><MetricTile label={label} value={value} unit={unit} /></Grid>)}
            </Grid>
            <ProvenanceNote>Lease and separately payable operating costs accrue with time, including overnight. Generation cost uses actual accepted output; at zero output its unit cost is shown as -. Monthly averages require energy coverage over the same elapsed billing month.</ProvenanceNote>
            {lease.coverage && <Typography variant="caption" color="text.secondary">Monthly production coverage: {formatValue(lease.coverage.coveragePct, '%')}</Typography>}
          </SectionCard>
          {scenarios.length > 0 && <FormControl size="small" sx={{ maxWidth: 360 }}>
            <InputLabel id="outage-scenario">Outage scenario</InputLabel>
            <Select labelId="outage-scenario" label="Outage scenario" value={scenario || 'standard'} onChange={event => setScenario(event.target.value)}>
              <MenuItem value="standard">None</MenuItem>
              {scenarios.map(item => <MenuItem key={item.scenarioName} value={item.scenarioName}>{item.scenarioName}</MenuItem>)}
            </Select>
          </FormControl>}
          <SectionCard title="Selected period customer costs" subtitle="Each measure uses the selected period; fixed and demand charges require a complete utility billing period.">
            <Grid container spacing={1}>
              {[
                ['Utility charges', summary.periodUtilityCost], ['Lease accrual', summary.periodLeaseCost], ['Customer cost', summary.periodCustomerCost],
                ['Average utility cost per served kWh', summary.averageUtilityEnergyCost], ['Average customer cost per served kWh', summary.averageCustomerEnergyCost],
              ].map(([label, measure]) => <Grid item xs={12} sm={6} md={4} key={label}><MetricTile label={label} value={measure?.value} unit={measure?.unit} measure={measure} /></Grid>)}
            </Grid>
            {lineItems.length > 0 && <TableContainer><Table size="small" aria-label="Cost line items">
              <TableHead><TableRow><TableCell>Charge</TableCell><TableCell>Basis</TableCell><TableCell align="right">Quantity</TableCell><TableCell align="right">Rate</TableCell><TableCell align="right">Amount</TableCell></TableRow></TableHead>
              <TableBody>{lineItems.map((item, index) => <TableRow key={index}><TableCell>{item.componentName || item.costCategory || item.componentType || item.kind}</TableCell><TableCell>{item.componentType?.replaceAll('_', ' ') || item.kind}</TableCell><TableCell align="right">{item.quantity || '-'}</TableCell><TableCell align="right">{item.rate || '-'}</TableCell><TableCell align="right">{item.amount ?? item.value ?? '-'}</TableCell></TableRow>)}</TableBody>
            </Table></TableContainer>}
            <ProvenanceNote>Calculated in SOMEAC from authorized observations and account inputs. Incomplete coverage leaves affected charges unpriced. Outage scenarios are separate from energy charges.</ProvenanceNote>
          </SectionCard>
          {hasUtilityAnalysis && <SectionCard title="Utility and optional analysis" subtitle={`Calculation: ${model.costs.calculationVersion || '-'}`}>
            <Grid container spacing={1}>
              <Grid item xs={12} sm={6} md={4}><MetricTile label="Import energy price" value={model.costs.importEnergyPricePerKwh} unit={model.costs.currency ? `${model.costs.currency}/kWh` : undefined} precision={4} measure={model.measures?.costs?.current?.importEnergyPricePerKwh} /></Grid>
              <Grid item xs={12} sm={6} md={4}><MetricTile label="Export energy credit" value={model.costs.exportCreditPerKwh} unit={model.costs.currency ? `${model.costs.currency}/kWh` : undefined} precision={4} measure={model.measures?.costs?.current?.exportCreditPerKwh} /></Grid>
              <Grid item xs={12} sm={6} md={4}><MetricTile label="Utility cost run rate" value={model.costs.utilityRatePerHour} unit={model.costs.currency ? `${model.costs.currency}/h` : undefined} provenance="Utility cost" /></Grid>
              <Grid item xs={12} sm={6} md={4}><MetricTile label="Period utility cost" value={model.costs.periodUtilityCost} unit={model.costs.currency} provenance="Calculated" /></Grid>
              <Grid item xs={12} sm={6} md={4}><MetricTile label="Average utility energy cost" value={model.costs.averageUtilityCostPerKwh} unit={model.costs.currency ? `${model.costs.currency}/kWh served` : undefined} provenance="Calculated" /></Grid>
              <Grid item xs={12} sm={6} md={4}><MetricTile label="Modeled LCOE" value={model.costs.modeledLcoePerKwh} unit={model.costs.currency ? `${model.costs.currency}/kWh generated` : undefined} provenance="Modeled" /></Grid>
              <Grid item xs={12} sm={6} md={4}><MetricTile label="Outage economic value" value={model.costs.outageEconomicValue} unit={model.costs.currency} provenance="Separate scenario" /></Grid>
            </Grid>
            <ProvenanceNote>Utility charges require an account tariff. Modeled lifetime LCOE and outage scenarios are optional analysis with separate assumptions.</ProvenanceNote>
          </SectionCard>}
        </Stack>
      )}

      {tab === 4 && (
        <SectionCard title="Events, reliability, and data quality">
          {model.events.length ? model.events.map((event, index) => (
            <Alert key={event.id || index} severity={event.severity === 'fault' ? 'error' : 'warning'} sx={{ mb: 1 }}>{event.message || event.code || 'Energy event'}</Alert>
          )) : <Alert severity="info">No recorded counter discontinuities are available for this range.</Alert>}
          <Grid container spacing={1} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}><MetricTile label="Coverage" value={model.meta.coveragePct} unit="%" /></Grid>
            <Grid item xs={12} sm={6}><MetricTile label="Latest period reconciliation residual" value={model.reconciliation.residualKwh} unit="kWh" state={model.reconciliation.status} /></Grid>
          </Grid>
        </SectionCard>
      )}

      <SectionCard title="Visible calculated energy rows" subtitle="The CSV action exports this bounded, authorized result" sx={{ mt: 2 }}>
        <TableContainer sx={{ maxHeight: 420 }}>
          <Table stickyHeader size="small">
            <TableHead><TableRow><TableCell>Period start</TableCell><TableCell>Metric</TableCell><TableCell align="right">Value</TableCell><TableCell>Status</TableCell><TableCell>Source</TableCell><TableCell align="right">Coverage</TableCell><TableCell align="right">Revision</TableCell></TableRow></TableHead>
            <TableBody>
              {rows.length ? rows.map((row, index) => (
                <TableRow key={row.id || `${row.time}-${row.metric}-${index}`}><TableCell>{row.time}</TableCell><TableCell>{row.metric || '-'}</TableCell><TableCell align="right">{formatValue(row.value, row.unit)}</TableCell><TableCell>{row.status === 'unavailable' ? '-' : row.status}</TableCell><TableCell>{row.sourceKind || '-'}</TableCell><TableCell align="right">{formatValue(row.coveragePct, '%')}</TableCell><TableCell align="right">{row.revision ?? '-'}</TableCell></TableRow>
              )) : <TableRow><TableCell colSpan={7}><Typography variant="body2" color="text.secondary">No calculated energy rows are available.</Typography></TableCell></TableRow>}
            </TableBody>
          </Table>
        </TableContainer>
      </SectionCard>
    </Box>
  );
}
